import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import LineCtaButton from '@/pages/gmb-audit/consult/components/LineCtaButton';
import {
  METRIC_DEFINITIONS,
  TOTAL_MAX_SCORE,
  GRADE_THRESHOLDS,
  PEER_SAMPLE_CONFIDENCE,
  gradeRangeText,
} from '@/constants/gmbMetrics';

// V1 Conversion-1.1｜公開方法說明頁同步 Backend 正式封版規格。
// - 11 Metrics 名稱 / 滿分 / 說明：與 calculate_gmb_audit_score 一致。
// - Grade：與 Report 使用的正式 Grade 名稱一致。
// - 移除開發者式流程狀態（state machine keys），改為使用者看得懂的 3 步驟。

const STEPS = [
  {
    icon: 'ri-search-line',
    title: '找到商家',
    desc: '輸入店家名稱與地區，從 Google Places 公開資料中找出正確的商家。',
  },
  {
    icon: 'ri-group-line',
    title: '整理公開資料並比較同業',
    desc: '整理商家的公開資訊，並依相同主要類別與可比較範圍取得附近同業樣本。',
  },
  {
    icon: 'ri-file-chart-line',
    title: '產出健診結果',
    desc: '依 11 項固定指標計算健康度，呈現 4 大診斷構面、同業差距與優先改善方向。',
  },
];

export default function GmbAuditMethodologyPage() {
  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="ds-container py-12 md:py-16">
          <p className="text-sm font-semibold uppercase tracking-wider text-primary-700">方法說明</p>
          <h1 className="mt-3 text-[2rem] font-bold leading-tight text-foreground-950 md:text-[2.75rem]">
            健診方法與資料說明
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-600">
            本工具依公開 Google Maps / Places 資料與天地人網自有評分規則，計算 0–100 的商家健康度，並提供與附近同業的比較。
          </p>
        </div>
      </section>

      <section className="ds-container py-12 md:py-16">
        <h2 className="text-[1.5rem] font-bold leading-snug text-foreground-950 md:text-[2.125rem]">
          評分架構（總分 {TOTAL_MAX_SCORE} 分）
        </h2>
        <p className="mt-3 max-w-3xl text-base leading-relaxed text-foreground-600">
          11 項指標各自配分，加總為 0–100 健康度。標示為「資料不足 (N/A)」的指標不列入總分計算，不會以 0 分呈現。
        </p>

        <div className="mt-6 scroll-x-clean rounded-xl border border-background-200">
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="border-b border-background-200 bg-background-100 text-left text-sm font-semibold text-foreground-700">
                <th className="px-4 py-3">代碼</th>
                <th className="px-4 py-3">健診指標</th>
                <th className="px-4 py-3">滿分</th>
                <th className="px-4 py-3">說明</th>
              </tr>
            </thead>
            <tbody>
              {METRIC_DEFINITIONS.map((m) => (
                <tr key={m.code} className="border-b border-background-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-foreground-500">{m.code}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-foreground-950">{m.name}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-foreground-900">{m.maxScore}</td>
                  <td className="px-4 py-3 text-sm leading-relaxed text-foreground-600">{m.description}</td>
                </tr>
              ))}
              <tr className="bg-background-100 font-semibold">
                <td className="px-4 py-3 text-foreground-500" />
                <td className="px-4 py-3 text-sm text-foreground-950">合計</td>
                <td className="px-4 py-3 text-sm text-foreground-950">{TOTAL_MAX_SCORE}</td>
                <td className="px-4 py-3" />
              </tr>
            </tbody>
          </table>
        </div>

        {/* 資料不足規則 */}
        <div className="mt-6 rounded-xl border border-background-200 bg-background-100 p-5 md:p-6">
          <p className="text-base leading-relaxed text-foreground-700">
            11 項指標依目前可取得的 Google Places 公開資料進行評估。若部分資料無法取得，該項可能標示為「資料不足」、「同業資料不足」或「不適用」，不會直接將缺少的資料一律視為 0 分。
          </p>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100">
        <div className="ds-container py-12 md:py-16">
          <h2 className="text-[1.5rem] font-bold leading-snug text-foreground-950 md:text-[2.125rem]">
            等級與同業樣本可信度
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-card md:p-6">
              <p className="text-base font-semibold text-foreground-900">等級門檻</p>
              <ul className="mt-4 space-y-3">
                {GRADE_THRESHOLDS.map((t) => (
                  <li key={t.grade} className="flex items-center gap-3">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-sm font-bold text-primary-900">
                      {t.grade}
                    </span>
                    <span className="text-base text-foreground-900">
                      <span className="font-semibold">{gradeRangeText(t)} 分</span>
                      <span className="ml-2 text-foreground-600">{t.label}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-card md:p-6">
              <p className="text-base font-semibold text-foreground-900">同業樣本可信度</p>
              <ul className="mt-4 space-y-3">
                {PEER_SAMPLE_CONFIDENCE.map((c) => (
                  <li key={c.key} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex shrink-0 items-center rounded-full bg-secondary-100 px-3 py-1 text-sm font-semibold text-secondary-900">
                      {c.label}
                    </span>
                    <span className="text-base leading-relaxed text-foreground-600">
                      <span className="font-semibold text-foreground-900">{c.sampleRange}</span>
                      <span className="ml-2">{c.description}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">
                同業比較所使用的樣本數會影響相對比較的參考程度；同業樣本不足時，相關同業比較指標可能不納入有效評分。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="ds-container py-12 md:py-16">
        <h2 className="text-[1.5rem] font-bold leading-snug text-foreground-950 md:text-[2.125rem]">
          健診怎麼進行？
        </h2>
        <p className="mt-3 max-w-3xl text-base leading-relaxed text-foreground-600">
          從找到商家到產出報告，整個流程只使用目前可取得的 Google Maps / Places 公開資料。
        </p>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {STEPS.map((s, index) => (
            <div key={s.title} className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-card">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-800">
                  <i className={`${s.icon} text-xl`} />
                </span>
                <span className="text-sm font-semibold uppercase tracking-wide text-foreground-400">
                  STEP {index + 1}
                </span>
              </div>
              <h3 className="mt-4 text-lg font-bold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-base leading-relaxed text-foreground-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="ds-container pb-16 md:pb-20">
        <div className="rounded-2xl border border-accent-200 bg-accent-50/70 p-5 md:p-7">
          <h2 className="text-lg font-bold text-foreground-950 md:text-xl">資料限制說明</h2>
          <p className="mt-3 text-base leading-relaxed text-foreground-700">
            本工具使用 Google Maps / Places 公開資料進行快速健診，不包含 Google Business Profile 後台的搜尋曝光、電話點擊、路線規劃等私有數據。
          </p>
          <p className="mt-2 text-base leading-relaxed text-foreground-700">
            本健診結果為天地人網依公開資料建立的自有評估模型，不是 Google 官方評分、搜尋排名或 SEO 排名。
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <Link
              to="/gmb-audit"
              className="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-700 px-6 text-base font-semibold text-background-50 transition-colors hover:bg-primary-800 sm:w-auto"
            >
              <i className="ri-flashlight-line text-lg" />
              開始免費健診
            </Link>
            <LineCtaButton fullWidth className="sm:w-auto" />
          </div>
          <p className="mt-4 text-base leading-relaxed text-foreground-600">
            想更了解健診結果代表什麼？也可以
            <Link to="/gmb-audit/consult" className="mx-1 font-semibold text-primary-700 underline underline-offset-2 hover:text-primary-800">
              預約諮詢
            </Link>
            或直接加入 LINE 與我們討論。
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}